import React, { Suspense } from 'react'
import { I18nextProvider } from 'react-i18next'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { persistor, store } from '@state/store'
import i18n from '../i18n'
import { ToastContainer } from 'react-toastify'
import { safeLazy } from 'react-safe-lazy'
import LoadingScreen from '@layout/LoadingScreen'
import { QueryClient, QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import LanguageManager from '@layout/LanguageManager'

import * as Sentry from '@sentry/react'
import ErrorFallback from '@layout/ErrorBoundaryComponent'
import { Auth0Provider } from '@auth0/auth0-react'

const queryClient = new QueryClient()

const domain = import.meta.env.VITE_AUTH0_DOMAIN!
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID!
const audience = import.meta.env.VITE_AUTH0_AUDIENCE!
const shouldShowDevtools = import.meta.env.VITE_REACT_APP_HIDE_DEVTOOLS !== 'true'

const FarmConnect = safeLazy(() => import('./Container'))
const UserCookies = safeLazy(() => import('@layout/UserCookies'))

const Application = () => {
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience: audience,
        redirect_uri: `${window.location.origin}/callback`,
      }}
      cacheLocation='memory'
    >
      <BrowserRouter>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <QueryErrorResetBoundary>
              {({ reset }) => (
                <Sentry.ErrorBoundary
                  onReset={reset}
                  fallback={({ error, resetError }) => <ErrorFallback error={error as Error} resetErrorBoundary={resetError} />}
                >
                  {/* @ts-ignore */}
                  <Provider store={store}>
                    <PersistGate loading={null} persistor={persistor}>
                      <Suspense fallback={<LoadingScreen />}>
                        <ToastContainer /* ... */ />
                        <LanguageManager />
                        <UserCookies />
                        <FarmConnect />
                      </Suspense>
                    </PersistGate>
                  </Provider>
                </Sentry.ErrorBoundary>
              )}
            </QueryErrorResetBoundary>
            {shouldShowDevtools && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
        </I18nextProvider>
      </BrowserRouter>
    </Auth0Provider>
  )
}

export default Application
